import { useMemo } from "react";
import { PRODUCTS } from "@/data/products";
import { ELEMENTS } from "./shared";

/**
 * Discipline accordion (Timber · Carpet · Tile · Stone) that lists every
 * product in the given `eligibleProducts` set. When a product is selected,
 * its colour chip strip is revealed inline underneath so the user can swap
 * colourways without leaving the accordion.
 *
 * All state is fully controlled by the parent so the accordion has no
 * memory of its own — makes it trivial to reuse in both modes.
 */
export default function ProductAccordion({
  eligibleProducts,
  productId,
  setProductId,
  colourIdx,
  setColourIdx,
  openElement,
  setOpenElement,
}) {
  const grouped = useMemo(() => {
    const g = { Timber: [], Carpet: [], Tile: [], Stone: [] };
    eligibleProducts.forEach((p) => g[p.discipline]?.push(p));
    return g;
  }, [eligibleProducts]);

  const selected = PRODUCTS.find((p) => p.id === productId);
  const selectedColours = selected?.colours || [];

  return (
    <div className="space-y-2" data-testid="viz-elements">
      {ELEMENTS.map((el) => {
        const items = grouped[el] || [];
        if (items.length === 0) return null;
        const open = openElement === el;
        return (
          <div key={el} className="border border-[#8C7355]/20">
            <button
              data-testid={`viz-element-${el.toLowerCase()}`}
              onClick={() => setOpenElement(open ? null : el)}
              className="w-full flex items-center justify-between px-4 py-3 text-[11px] tracking-[0.3em] uppercase text-[#F5F4F0]"
              style={{ background: open ? "rgba(140,115,85,0.12)" : "transparent" }}
            >
              <span>{el}</span>
              <span className="text-[#C9A876]/70">{items.length} · {open ? "−" : "+"}</span>
            </button>
            {open && (
              <div className="p-2 space-y-1 max-h-[320px] overflow-y-auto">
                {items.map((p) => {
                  const on = p.id === productId;
                  return (
                    <div key={p.id}>
                      <button
                        data-testid={`viz-product-${p.id}`}
                        onClick={() => setProductId(p.id)}
                        className="w-full text-left px-3 py-2 border flex items-center gap-3"
                        style={{
                          borderColor: on ? "#C9A876" : "rgba(214,209,202,0.12)",
                          background: on ? "rgba(201,168,118,0.12)" : "transparent",
                        }}
                      >
                        <img
                          src={p.swatch}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          width="40"
                          height="40"
                          className="w-10 h-10 object-cover flex-shrink-0"
                        />
                        <span className="flex-1 min-w-0">
                          <span className="block font-serif-display text-[#F5F4F0] text-sm leading-tight truncate">{p.name}</span>
                          <span className="block text-[#D6D1CA]/50 text-[9px] tracking-[0.2em] uppercase mt-0.5 truncate">{p.tag}</span>
                        </span>
                      </button>
                      {on && (p.colours?.length > 1) && (
                        <div
                          className="mt-2 mb-1 px-3 flex items-center gap-2 flex-wrap"
                          data-testid={`viz-colours-${p.id}`}
                        >
                          {p.colours.map((c, i) => {
                            const active = i === colourIdx;
                            return (
                              <button
                                key={c.name}
                                data-testid={`viz-colour-${p.id}-${i}`}
                                onClick={() => setColourIdx(i)}
                                title={c.name}
                                aria-label={c.name}
                                className="relative w-6 h-6 rounded-full transition-transform duration-200 hover:scale-110"
                                style={{
                                  background: c.hex,
                                  boxShadow: active
                                    ? "0 0 0 1.5px #121315, 0 0 0 3px #C9A876"
                                    : "inset 0 0 0 1px rgba(214,209,202,0.35)",
                                }}
                              />
                            );
                          })}
                          <span className="text-[#D6D1CA]/60 text-[9px] tracking-[0.28em] uppercase font-body ml-1">
                            {selectedColours[colourIdx]?.name}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
